import React, { useMemo, useRef, useState } from 'react';
import {
  useUsersAndGroupsContext,
  useGetSession,
  UsersAndGroupsContextProvider,
} from '@opengov/bnp-core';
import { useQueryClient } from '@tanstack/react-query';
import {
  getSortedOptions,
  getSubjectStoryClaimsToDelete,
  getUpdatedStoryPermissionsOnOptionsSelected,
  updateStoryClaimsForSubject,
} from '../../../stories/utils/subjectSelect';
import { ISubjectSelectOption } from '../../../types/subjectTypes';
import {
  ISubjectStoryClaimsList,
  IStoryPrivilege,
  IsubjectClaimsMessages,
  ISubjectStoryClaim,
  ISubjectPublicationClaim,
} from '../../../types';
import { StoryGranularAccessDialog } from '../../../stories/components/StoryAccessGranularDialog/StoryAccessGranularDialog';
import { sanitizeSectionIdsForClaims } from '@opengov/stories-shared/utils';
import { STORIES_EDITOR_PRIVILEGE, STORIES_PDF_GENERATION_PRIVILEGE, STORIES_PUBLISHER_PRIVILEGE, STORIES_VIEWER_PRIVILEGE } from '@opengov/stories-shared/constants';
import {
  useDeleteSubjectStoryClaim,
  useSaveSubjectStoryClaim,
  useSubjectPublicationClaims,
  useSubjectStoryClaims,
  subjectClaimsQueryKeys
} from '../../../stories/hooks/subjectClaims';
import { useStory } from '../../../stories/components/StoryView/hooks';
import { fetchStoryClaims } from '../../../stories/api/subjectClaimsApi';
export interface IStoryGranularAccessProps {
  closeModal(): void;
  entitySubdomain: string;
  storyId: string;
  isTemplate: boolean;
  entityId: number;
  messages: IsubjectClaimsMessages;
  publicationId: string;
  onSuccess?(): void;
}

export const StoryGranularAccessView: React.FC<IStoryGranularAccessProps> = (props: IStoryGranularAccessProps) => {
  const { closeModal, storyId, isTemplate, entityId, messages, publicationId, onSuccess } = props;
  const queryClient = useQueryClient();
  const saveAccessMutation = useSaveSubjectStoryClaim();
  const deleteAccessMutation = useDeleteSubjectStoryClaim();
  const { data: story } = useStory(
    entityId > 0 && storyId ? { storyId, isTemplate } : null,
    messages.somethingWentWrongError
  );
  const { data: fetchedStoryClaims } = useSubjectStoryClaims(storyId || null);
  const { data: subjectPublicationClaimsList } = useSubjectPublicationClaims(
    publicationId
      ? { publicationId, publicationClaims: [messages.manageSharedAndViewAll, messages.manageAll] }
      : null
  );
  const [editedStoryClaims, setEditedStoryClaims] = useState<ISubjectStoryClaimsList | null>(null);
  const editableStoryClaimsList = editedStoryClaims ?? fetchedStoryClaims ?? [];
  const [selectedSubjectStoryClaimsList, setSelectedSubjectStoryClaimsList] = useState<ISubjectStoryClaimsList>([]);
  const [subjectIdsToDelete, setSubjectIdsToDelete] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputName = 'subjectSelect';

  const { activeUsers, groupWithMembers, usersMap, groupsMap } = useUsersAndGroupsContext();
  const { data: session } = useGetSession();

  const combinedSubjectsToRender = useMemo(() => {
    const existingSubjectIds = new Set(editableStoryClaimsList.map((claim) => claim.subjectId));
    
    const additionalClaims = (subjectPublicationClaimsList ?? [])
      .filter((pubClaim: ISubjectPublicationClaim) => !existingSubjectIds.has(pubClaim.subjectId))
      .map(pubClaim => {
        const baseStoryClaim: ISubjectStoryClaim = {
          subjectId: pubClaim.subjectId,
          subjectType: pubClaim.subjectType,
          storyId,
          storyClaims: []
        };
  
        if (pubClaim.publicationClaim === messages.manageSharedAndViewAll) {
          baseStoryClaim.storyClaims = [STORIES_VIEWER_PRIVILEGE];
        } else if (pubClaim.publicationClaim === messages.manageAll) {
          baseStoryClaim.storyClaims = [
            STORIES_VIEWER_PRIVILEGE,
            STORIES_EDITOR_PRIVILEGE,
            STORIES_PDF_GENERATION_PRIVILEGE,
            STORIES_PUBLISHER_PRIVILEGE
          ];
        }
  
        return baseStoryClaim;
      });
  
    return [...editableStoryClaimsList, ...additionalClaims];
  }, [editableStoryClaimsList, subjectPublicationClaimsList, storyId, messages]);

  const sortedOptionsList = useMemo(() => {
    return getSortedOptions({ session, users: activeUsers, groups: groupWithMembers, subjectStoryClaimsList: combinedSubjectsToRender });
  }, [session, activeUsers, groupWithMembers, combinedSubjectsToRender]);

  const onOptionsSelected = (options: ISubjectSelectOption[]) => {
    if (!options.length) {
      return;
    }
    const [updatedClaims, updatedSelected] = getUpdatedStoryPermissionsOnOptionsSelected(combinedSubjectsToRender, selectedSubjectStoryClaimsList, options, storyId);
    setEditedStoryClaims(updatedClaims);
    setSelectedSubjectStoryClaimsList(updatedSelected);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, subjectId: string) => {
    const privilege = e.target.name as IStoryPrivilege;
    const checked = e.target.checked;
    const { updatedClaims, updatedSelectedClaims } = updateStoryClaimsForSubject(subjectId, privilege, checked, combinedSubjectsToRender, selectedSubjectStoryClaimsList);
    setEditedStoryClaims(updatedClaims);
    setSelectedSubjectStoryClaimsList(updatedSelectedClaims);
  };

  const handleClick = (subjectId: string) => {
    const { updatedSubjectIdsToDelete, updatedClaims, updatedSelectedClaims } = getSubjectStoryClaimsToDelete(subjectId, combinedSubjectsToRender, selectedSubjectStoryClaimsList, subjectIdsToDelete);
    setSubjectIdsToDelete(updatedSubjectIdsToDelete);
    setEditedStoryClaims(updatedClaims);
    setSelectedSubjectStoryClaimsList(updatedSelectedClaims);
  };

  const handleSave = async () => {
    try {
      let didMutate = false;
      if (selectedSubjectStoryClaimsList.length > 0) {
        await saveAccessMutation.mutateAsync({
          subjectStoryClaimsList: selectedSubjectStoryClaimsList,
          publicationId,
          sectionIds: story?.folderId ? sanitizeSectionIdsForClaims([story.folderId]) : [],
          notifyNewCollaborators: true
        });
        didMutate = true;
      }
      if (subjectIdsToDelete.length > 0) {
        await deleteAccessMutation.mutateAsync({ subjectIds: subjectIdsToDelete, storyId });
        didMutate = true;
      }
      if (didMutate) {
        await queryClient.fetchQuery({
          queryKey: subjectClaimsQueryKeys.storyClaims(storyId),
          queryFn: () => fetchStoryClaims(storyId)
        });
        onSuccess?.();
      }
      closeModal();
    } catch {
      // Toast handled by mutation hooks.
    }
  };

  const cols: { title: string; alignment: 'left' | 'center'; width: string }[] = [
    { title: messages.name, alignment: 'left', width: '50%' },
    { title: messages.view, alignment: 'center', width: '10%' },
    { title: messages.edit, alignment: 'center', width: '10%' },
    { title: messages.export, alignment: 'center', width: '10%' },
    { title: messages.publish, alignment: 'center', width: '10%' },
    { title: '', alignment: 'center', width: '10%' },
  ];

  return (
    <StoryGranularAccessDialog
      isBulk={false}
      storyTitles={[]}
      inputRef={inputRef}
      inputName={inputName}
      messages={messages}
      sortedOptionsList={sortedOptionsList}
      cols={cols}
      subjectsToRender={combinedSubjectsToRender}
      usersMap={usersMap}
      groupsMap={groupsMap}
      onClose={closeModal}
      onSave={handleSave}
      onOptionChange={(_, value) => onOptionsSelected(value)}
      onPrivilegeChange={handleChange}
      onRemoveClick={handleClick}
      subjectPublicationClaimsList={subjectPublicationClaimsList}
    />
  );
};

export const StoryGranularAccess = (props: IStoryGranularAccessProps) => {
  const { entitySubdomain } = props;
  return (
    <UsersAndGroupsContextProvider entitySubdomain={entitySubdomain}>
      <StoryGranularAccessView {...props} />
    </UsersAndGroupsContextProvider>
  );
};
